import { EventBus } from '@/event-bus'
import { i18n } from '../i18n'

export function getStatusColor(status) {
  const colors = { SUCCESS: 'success', WARNING: 'warning', ERROR: 'error' }
  return colors[status] || 'grey'
}

/**
 * Returns the Vuetify background color class matching a trigger status.
 */
export function getTriggerStatusBackgroundColor(status) {
  switch (status) {
    case 'error':
    case 'relaunch_failed':
      return 'red lighten-5'
    case 'success':
    case 'relaunched':
      return 'green lighten-5'
    case 'processing':
      return 'orange lighten-5'
    default:
      return 'grey lighten-3'
  }
}

/**
 * Returns the Vuetify text color class matching a trigger status.
 */
export function getTriggerStatusTextColor(status) {
  switch (status) {
    case 'error':
    case 'relaunch_failed':
      return 'red darken-1'
    case 'success':
    case 'relaunched':
      return 'green darken-4'
    case 'processing':
      return 'orange darken-4'
    default:
      return 'grey darken-4'
  }
}

/**
 * Formats a currency amount into a human-readable, localized string.
 *
 * @param rawValue
 * @param {string} [currencyCode] - An optional ISO 4217 currency code (e.g. "EUR", "USD")
 *
 * @returns {string} A localized string (e.g. "€85.50" or "85,50 €" depending on locale)
 */
export const formatCurrencyAmount = (rawValue, currencyCode) => {
  const locale = navigator.language

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode ?? 'EUR',
  }).format(rawValue)
}

export const displayMessage = (text, color = 'error') => {
  EventBus.$emit('displaySnackbar', {
    display: true,
    text,
    color,
  })
}

export const weekDays = () => {
  return {
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
    sunday: [],
  }
}

const CONVERSION_FACTORS = {
  km: 1000,
  miles: 1609.344,
  m: 1,
}

export function isNumeric(val) {
  return val !== null && val !== '' && !Number.isNaN(Number(val))
}

/**
 * Sort languages with English first, then alphabetically by name
 */
export function sortLanguages(languages) {
  return [...languages].sort((a, b) => {
    if (a.code === 'en') return -1
    if (b.code === 'en') return 1
    return a.name.localeCompare(b.name)
  })
}

export function convert(value, fromUnit, toUnit) {
  if (!isNumeric(value)) return 0

  const fromFactor = CONVERSION_FACTORS[fromUnit] || 1
  const toFactor = CONVERSION_FACTORS[toUnit] || 1

  return (Number(value) * fromFactor) / toFactor
}

export function textExists(val) {
  return typeof val === 'string' && val.trim().length > 0
}

/**
 * Return a valid value or a fallback value.
 *
 * @param {string} value - Incoming value.
 * @param {string} fallback - Fallback value.
 * @returns {string} Valid value.
 */
export function resolveValue(value, fallback) {
  return textExists(value) ? value : fallback
}

export function getOrderDocTypeTranslation(documentTypeTechnicalName) {
  const translations = {
    invoice: i18n.t('order.documents.invoice'),
    warranty: i18n.t('order.documents.warranty'),
    credit_note: i18n.t('order.documents.creditNote'),
    other: i18n.t('order.documents.other'),
  }
  return translations[documentTypeTechnicalName] || documentTypeTechnicalName
}

/**
 * Convert dimensions from cm to mm
 */
export function convertDimensionsToMm(length, width, height, unit) {
  if (unit === 'cm') {
    return {
      length: length != null ? length * 10 : length,
      width: width != null ? width * 10 : width,
      height: height != null ? height * 10 : height
    }
  }
  return { length, width, height }
}

/**
 * Convert weight from kg to g
 */
export function convertWeightToGrams(weight, unit) {
  if (unit === 'kg' && weight != null) {
    return weight * 1000
  }
  return weight
}

/**
 * Check if empty weight exceeds max weight
 * @returns {boolean} true if empty weight > max weight
 */
export function isEmptyWeightExceedingMax(emptyWeight, weightUnit, maxWeight, maxWeightUnit) {
  if (emptyWeight == null || emptyWeight === '' || maxWeight == null || maxWeight === '') return false
  const emptyWeightInGrams = convertWeightToGrams(emptyWeight, weightUnit)
  const maxWeightInGrams = convertWeightToGrams(maxWeight, maxWeightUnit)
  return emptyWeightInGrams >= maxWeightInGrams
}

/**
 * Filter out empty/undefined labels
 */
export function filterEmptyLabels(labels) {
  return Object.fromEntries(
    Object.entries(labels || {})
      .filter(([, value]) => typeof value === 'string' && value.trim() !== '')
      .map(([key, value]) => [key, value.trim()])
  )
}

/**
 * Convert form values to API format for box
 */
export function convertBoxToApiFormat(values) {
  const dimensions = convertDimensionsToMm(values.length, values.width, values.height, values.dimensionUnit)
  const emptyWeight = convertWeightToGrams(values.emptyWeight, values.weightUnit)
  const maxWeight = convertWeightToGrams(values.maxWeight, values.maxWeightUnit)
  const labels = filterEmptyLabels(values.labels)

  return {
    activate: values.activate,
    reference: values.reference,
    labels,
    dimensions: {
      ...dimensions,
      display_unit: values.dimensionUnit
    },
    empty_weight: emptyWeight,
    empty_weight_display_unit: values.weightUnit,
    max_weight: maxWeight,
    max_weight_display_unit: values.maxWeightUnit
  }
}

/**
 * Convert API format to form values for box editing
 * @param {Object} apiValues - The API format box object
 * @param {string} locale - Current locale (e.g., 'fr', 'en')
 */
export function convertBoxFromApiFormat(apiValues, locale = 'en') {
  if (!apiValues) return null

  const dimensionUnit = apiValues.dimensions?.display_unit || 'mm'
  const weightUnit = apiValues.empty_weight_display_unit || 'g'
  const maxWeightUnit = apiValues.max_weight_display_unit || 'kg'

  // Convert dimensions from mm to display unit
  let length = apiValues.dimensions?.length
  let width = apiValues.dimensions?.width
  let height = apiValues.dimensions?.height
  if (dimensionUnit === 'cm') {
    length = length != null ? length / 10 : length
    width = width != null ? width / 10 : width
    height = height != null ? height / 10 : height
  }

  // Convert weight from g to display unit
  let emptyWeight = apiValues.empty_weight
  if (weightUnit === 'kg' && emptyWeight != null) {
    emptyWeight = emptyWeight / 1000
  }

  // Convert max weight from g to display unit
  let maxWeight = apiValues.max_weight
  if (maxWeightUnit === 'kg' && maxWeight != null) {
    maxWeight = maxWeight / 1000
  }

  // Get name from labels based on current locale, fallback to English, then reference
  const labels = apiValues.labels || {}
  const name = labels[locale] || labels.en || apiValues.reference || ''

  return {
    id: apiValues.id,
    name,
    activate: apiValues.activate ?? false,
    reference: apiValues.reference,
    labels,
    length,
    width,
    height,
    dimensionUnit,
    emptyWeight,
    weightUnit,
    maxWeight,
    maxWeightUnit,
    defaultLanguage: apiValues.defaultLanguage || 'en',
  }
}

/**
 * Convert preparation settings from API format to form format
 * @param {Object} apiValues - The API format preparation object
 * @param {string} locale - Current locale (e.g., 'fr', 'en')
 */
export function convertPreparationFromApiFormat(apiValues, locale = 'en') {
  if (!apiValues) return null

  return {
    boxForPreparation: apiValues.box_selection?.activate ?? false,
    boxForPreparationTypes: apiValues.box_selection?.order_types || [],
    boxTypes: (apiValues.box_types || []).map(box => convertBoxFromApiFormat(box, locale)),
  }
}

/**
 * Format a box entry label for display (e.g. "Medium Box - 1")
 * @param {Object} entry - Box entry with boxType and index
 * @param {string} locale - Current i18n locale
 */
export function formatBoxEntryLabel(entry, locale) {
  const name =
    (entry.boxType.labels && (entry.boxType.labels[locale] || entry.boxType.labels.en)) ||
    entry.boxType.reference
  return `${name} - ${entry.index}`
}

/**
 * Convert preparation settings from form format to API format
 * @param {Object} values - The form format preparation object
 */
export function convertPreparationToApiFormat(values) {
  return {
    box_selection: {
      activate: values.boxForPreparation ?? false,
      order_types: values.boxForPreparationTypes || [],
    },
    box_types: (values.boxTypes || []).map(box => convertBoxToApiFormat(box)),
  }
}
